import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function ContactFormSection() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    investmentGoal: "",
    budget: ""
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        mode: "cors",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          access_key: "f40c9802-1eb9-488c-84b6-5d46cc60e552",
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          investment_goal: formData.investmentGoal,
          budget: formData.budget,
          subject: "New Investment Inquiry - Motor City Dubai",
        }),
      });

      const result = await response.json();

      if (result.success) {
        setIsSuccess(true);
        
        // Increment investor counter
        const currentCount = parseInt(localStorage.getItem('investorCount') || '23');
        const newCount = currentCount + 1;
        localStorage.setItem('investorCount', newCount.toString());
        window.dispatchEvent(new CustomEvent('investorFormSubmitted'));

        toast.success("Thank you! Your inquiry has been received.");
      } else {
        throw new Error(result.message || "Submission failed");
      }
    } catch (error) {
      console.error("Form submission error:", error);
      toast.error("Failed to submit form. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success state
  if (isSuccess) {
    return (
      <section id="contact-form" className="py-20 bg-gradient-to-br from-primary/10 to-accent/10 scroll-mt-20">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <div className="bg-card rounded-2xl p-12 border border-border shadow-xl text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-12 h-12 text-green-600" />
              </div>
              <h2 className="text-4xl font-bold mb-4 text-foreground">Thank You!</h2>
              <p className="text-lg text-muted-foreground mb-8">
                Your investment inquiry has been successfully submitted.
              </p>
              <h3 className="font-bold text-lg mb-3">What Happens Next?</h3>
              <ul className="text-left space-y-2 text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">1.</span>
                  <span>You'll receive a detailed investment pack via email within 5 minutes</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">2.</span>
                  <span>Our specialist will call you within 2 hours to answer questions</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">3.</span>
                  <span>Schedule a virtual tour and receive personalized ROI projections</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="contact-form" className="py-20 bg-gradient-to-br from-primary/10 to-accent/10 scroll-mt-20">
      <div className="container">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-foreground">
              Start Your Dubai Real Estate Investment Journey
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground">
              Request Your Free Guide to High ROI Dubai Apartments
            </p>
          </div>

          <div className="bg-card rounded-2xl p-8 md:p-12 border border-border shadow-xl">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="mb-6">
                <h3 className="text-2xl font-bold mb-2">Get Your Free Dubai Property Investment Guide</h3>
                <p className="text-muted-foreground">Enter your details to receive a comprehensive guide on this exclusive Dubai investment opportunity.</p>
              </div>

              {/* Full Name */}
              <div>
                <Label htmlFor="name" className="text-base font-semibold">
                  Full Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="John Doe"
                  required
                  autoComplete="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="mt-2 h-12"
                />
              </div>

              {/* Email */}
              <div>
                <Label htmlFor="email" className="text-base font-semibold">
                  Email <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="john@example.com"
                  required
                  autoComplete="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="mt-2 h-12"
                />
              </div>

              {/* Phone Number */}
              <div>
                <Label htmlFor="phone" className="text-base font-semibold">
                  Phone Number with Country Code <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="+1 234 567 8900"
                  required
                  autoComplete="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="mt-2 h-12"
                />
              </div>

              {/* Investment Goal */}
              <div>
                <Label htmlFor="goal" className="text-base font-semibold">
                  What is your goal for this Dubai property investment?
                </Label>
                <Select
                  value={formData.investmentGoal}
                  onValueChange={(value) => setFormData({ ...formData, investmentGoal: value })}
                >
                  <SelectTrigger className="mt-2 h-12" aria-label="Select your primary investment goal">
                    <SelectValue placeholder="Select your goal" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rental-income">Achieve 10% Rental Income in Dubai</SelectItem>
                    <SelectItem value="secure-capital">Make a Secure USD Investment in Dubai</SelectItem>
                    <SelectItem value="golden-visa">Obtain a Dubai Golden Visa Through Property</SelectItem>
                    <SelectItem value="diversification">Portfolio Diversification</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Investment Budget */}
              <div>
                <Label htmlFor="budget" className="text-base font-semibold">
                  What is your budget for this Dubai property for sale?
                </Label>
                <Select
                  value={formData.budget}
                  onValueChange={(value) => setFormData({ ...formData, budget: value })}
                >
                  <SelectTrigger className="mt-2 h-12" aria-label="Select your estimated investment budget">
                    <SelectValue placeholder="Select your budget" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="250k-400k">$250,000 - $400,000</SelectItem>
                    <SelectItem value="400k-700k">$400,000 - $700,000</SelectItem>
                    <SelectItem value="700k-1m">$700,000 - $1,000,000</SelectItem>
                    <SelectItem value="1m-plus">$1,000,000+</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                size="lg"
                className="w-full text-lg py-6"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Sending...
                  </>
                ) : (
                  "GET MY FREE DUBAI INVESTMENT GUIDE"
                )}
              </Button>

              <p className="text-sm text-muted-foreground text-center">
                By submitting, you agree to be contacted by Suite Global Estate regarding your investment inquiry.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
